import React, { useState } from 'react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { BridgeStatsChart } from '@/components/bridge-stats-chart';
import { AppSidebar } from '@/components/app-sidebar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/breadcrumb';
import { Separator } from '@/components/separator';
import {
    SidebarInset,
    SidebarProvider,
    SidebarTrigger,
} from '@/components/sidebar';
import { RadioGroup, RadioGroupItem } from '@/components/radio-group';
import { Label } from '@/components/label';
import { Link } from 'react-router-dom';
import theme from '@/theme/theme';

export default function BlockchainBridgeStatsPage() {
    const [metricType, setMetricType] = useState<'volume' | 'transfers'>('volume');
    const [timePeriod, setTimePeriod] = useState<'24h' | '7d' | '30d' | 'all'>('24h');

    return (
        <SidebarProvider>
            <AppSidebar />
            <SidebarInset>
                <div className="px-4 md:px-6 lg:px-8 @container">
                    <div className="w-full max-w-6xl mx-auto">
                        <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
                            <div className="flex flex-1 items-center gap-2">
                                <SidebarTrigger className="-ms-1" />
                                <div className="max-lg:hidden lg:contents">
                                    <Separator
                                        orientation="vertical"
                                        className="me-2 data-[orientation=vertical]:h-4"
                                    />
                                    <Breadcrumb>
                                        <BreadcrumbList>
                                            <BreadcrumbItem className="hidden md:block">
                                                <BreadcrumbLink asChild>
                                                    <Link to="/">Home</Link>
                                                </BreadcrumbLink>
                                            </BreadcrumbItem>
                                            <BreadcrumbSeparator className="hidden md:block" />
                                            <BreadcrumbItem>
                                                <BreadcrumbLink asChild>
                                                    <Link to="/blockchain">Blockchain</Link>
                                                </BreadcrumbLink>
                                            </BreadcrumbItem>
                                            <BreadcrumbSeparator className="hidden md:block" />
                                            <BreadcrumbItem>
                                                <BreadcrumbPage>Bridge Stats</BreadcrumbPage>
                                            </BreadcrumbItem>
                                        </BreadcrumbList>
                                    </Breadcrumb>
                                </div>
                            </div>
                            <ConnectButton />
                        </header>

                        <div className="py-6">
                            <div className="flex items-center mb-6">
                                <div className={`w-1 h-8 bg-[${theme.colors.primary.hex}] mr-3`}></div>
                                <h1 className="text-3xl font-bold">Bridge Statistics</h1>
                            </div>

                            <p className="text-muted-foreground mb-8">
                                Visualize cross-chain bridge transaction flow between different blockchain networks
                            </p>

                            {/* Filters */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                {/* Metric Type Filter */}
                                <div className="rounded-lg border bg-card p-6">
                                    <h3 className="text-lg font-semibold mb-4">Metric Type</h3>
                                    <RadioGroup
                                        value={metricType}
                                        onValueChange={(value) => setMetricType(value as 'volume' | 'transfers')}
                                        className="flex flex-col gap-3"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="volume" id="volume" />
                                            <Label htmlFor="volume" className="cursor-pointer font-normal">
                                                Transaction Volume
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="transfers" id="transfers" />
                                            <Label htmlFor="transfers" className="cursor-pointer font-normal">
                                                Number of Transfers
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                </div>

                                {/* Time Period Filter */}
                                <div className="rounded-lg border bg-card p-6">
                                    <h3 className="text-lg font-semibold mb-4">Time Period</h3>
                                    <RadioGroup
                                        value={timePeriod}
                                        onValueChange={(value) => setTimePeriod(value as '24h' | '7d' | '30d' | 'all')}
                                        className="flex flex-col gap-3"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="24h" id="24h" />
                                            <Label htmlFor="24h" className="cursor-pointer font-normal">
                                                Last 24 Hours
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="7d" id="7d" />
                                            <Label htmlFor="7d" className="cursor-pointer font-normal">
                                                Last 7 Days
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="30d" id="30d" />
                                            <Label htmlFor="30d" className="cursor-pointer font-normal">
                                                Last 30 Days
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <RadioGroupItem value="all" id="all" />
                                            <Label htmlFor="all" className="cursor-pointer font-normal">
                                                All Time
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                </div>
                            </div>

                            {/* Chart Section */}
                            <div className="mt-8">
                                <div className="flex items-center mb-4">
                                    <div className={`w-1 h-6 bg-[${theme.colors.primary.hex}] mr-3`}></div>
                                    <h2 className="text-2xl font-bold">Cross-Chain Flow</h2>
                                </div>
                                <BridgeStatsChart metricType={metricType} timePeriod={timePeriod} />
                            </div>
                        </div>
                    </div>
                </div>
            </SidebarInset>
        </SidebarProvider>
    );
}
